import { useCallback, useEffect, useState } from "react";
import { VerticalContainer, HorizontalContainer, TableHeaderContainer, TableHeaderCell, CardContainer, TableRowContainer } from "@shared/designSystem/Containers";
import { Text } from "@shared/designSystem/Texts";
import { ComboBox, ComboBoxOption } from "@shared/designSystem/ComboBox";
import { SystemIcon } from "@shared/designSystem/SystemIcon";
import UserCard from "@shared/designSystem/UserCard";
import Button, { HollowedButton, NormalButton, RefreshButton } from "@shared/designSystem/Button";
import SearchBar from "@shared/designSystem/SearchBar";
import { TextAreaInput } from "@shared/designSystem/TextInput";
import { Modal } from "@shared/designSystem/messages/Modal";
import { Loader } from "@shared/designSystem/Loader";
import { ReactComponent as QontySVG } from "@shared/assets/SVGs/qonty.svg";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { Application } from "@core/Application";
import { useAuth } from "@contexts/authContext";
import { QuestionFilter, QuestionRecord } from "@services/QuestionsService";
import styles from "./Consultas.module.css";

interface ConsultaRow {
    id: string;
    name: string;
    date: string;
    time: string;
    responseDate: string;
    responseTime: string;
    respondedBy: string;
    preview: string;
    message: string;
    imageURL?: string;
    read: boolean;
    responded: boolean;
    archived: boolean;
    response: string;
}

const filterOptions: ComboBoxOption[] = [
    { id: "inbox", title: "Recibidas" },
    { id: "read", title: "Leídas" },
    { id: "responded", title: "Respondidas" },
    { id: "archived", title: "Archivadas" },
];

const filterIdToServiceFilter: Record<string, QuestionFilter> = {
    inbox: "inbox",
    read: "read",
    responded: "responded",
    archived: "archived",
};

const modalSize = {
    width: "92%",
    maxWidth: "980px",
    maxHeight: "82vh",
} as const;

const Consultas: React.FC<{ orgID: string }> = ({ orgID }) => {
    const { user } = useAuth();
    const [consultas, setConsultas] = useState<Array<ConsultaRow>>([]);
    const [adminNamesById, setAdminNamesById] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedConsulta, setSelectedConsulta] = useState<ConsultaRow | null>(null);
    const [responseText, setResponseText] = useState("");
    const [showFeedback, setShowFeedback] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState("¡Respuesta enviada!");
    const [selectedFilter, setSelectedFilter] = useState<ComboBoxOption>(filterOptions[0]);
    const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
    const [selectedResponseConsulta, setSelectedResponseConsulta] = useState<ConsultaRow | null>(null);
    const [searchQuery, setSearchQuery] = useState("");

    const columnWidths = {
        collaborator: "26%",
        date: "14%",
        time: "12%",
        preview: "36%",
        action: "12%",
    };
    const questionsService = Application.shared().coreDependencies.questionsService;
    const usersService = Application.shared().coreDependencies.usersService;

    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    const filteredConsultas = consultas.filter((row) => {
        if (!normalizedSearchQuery) return true;

        return [row.name, row.preview, row.message, row.date, row.time]
            .some((value) => value.toLowerCase().includes(normalizedSearchQuery));
    });

    const getServiceFilter = (filterId: string): QuestionFilter => {
        return filterIdToServiceFilter[filterId] ?? "inbox";
    };

    const findConsultaById = (rowId: string) => {
        return consultas.find((item) => item.id === rowId) ?? null;
    };

    const parseTimestamp = (timestamp?: number) => {
        if (!timestamp) return null;
        const normalizedTimestamp = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
        const date = new Date(normalizedTimestamp);
        if (Number.isNaN(date.getTime())) return null;
        return date;
    };

    const formatDate = (timestamp?: number) => {
        const date = parseTimestamp(timestamp);
        if (!date) return "-";
        return date.toLocaleDateString("es-AR");
    };

    const formatTime = (timestamp?: number) => {
        const date = parseTimestamp(timestamp);
        if (!date) return "-";
        return date.toLocaleTimeString("es-AR", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    };

    const buildPreview = (text: string, max = 80) => {
        if (text.length <= max) return text;
        return `${text.slice(0, max).trim()}...`;
    };

    const extractMessageText = (rawValue: unknown): string => {
        const pickFromObject = (value: Record<string, unknown>): string | null => {
            const preferredKeys = ["comments", "message", "text", "body"];
            for (const key of preferredKeys) {
                const candidate = value[key];
                if (typeof candidate === "string" && candidate.trim()) {
                    return candidate;
                }
            }
            return null;
        };

        if (typeof rawValue === "string") {
            const trimmed = rawValue.trim();
            if (!trimmed) return "";

            if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
                try {
                    const parsed = JSON.parse(trimmed) as unknown;
                    const parsedText = extractMessageText(parsed);
                    if (parsedText.trim()) return parsedText;
                } catch (error) {
                    const doubleQuotedMatch = trimmed.match(/"comments"\s*:\s*"([^"]*)"/i);
                    if (doubleQuotedMatch?.[1]) return doubleQuotedMatch[1];

                    const singleQuotedMatch = trimmed.match(/'comments'\s*:\s*'([^']*)'/i);
                    if (singleQuotedMatch?.[1]) return singleQuotedMatch[1];
                }
            }
            return trimmed;
        }

        if (Array.isArray(rawValue)) {
            const collected = rawValue
                .map((item) => extractMessageText(item))
                .filter((item) => item.trim().length > 0);
            return collected.join(" ");
        }

        if (rawValue && typeof rawValue === "object") {
            const objectValue = rawValue as Record<string, unknown>;
            const preferred = pickFromObject(objectValue);
            if (preferred) return preferred;

            try {
                return JSON.stringify(objectValue);
            } catch (error) {
                return String(objectValue);
            }
        }

        if (rawValue === null || rawValue === undefined) return "";
        return String(rawValue);
    };

    const extractOutboundAnswerText = (rawResponse: string): string => {
        const normalizedResponse = rawResponse.trim();
        if (!normalizedResponse) return "";

        const responseBlockMatch = normalizedResponse.match(/(?:^|\n)\s*Respuesta:\s*([\s\S]*)$/i);
        if (responseBlockMatch?.[1]) {
            return responseBlockMatch[1].trim();
        }

        return normalizedResponse;
    };

    const selectedConsultaMessage = extractMessageText(selectedConsulta?.message ?? "");
    const selectedResponseConsultaMessage = extractMessageText(selectedResponseConsulta?.message ?? "");
    const selectedResponseText = extractOutboundAnswerText(
        extractMessageText(selectedResponseConsulta?.response ?? ""),
    );
    const resolveResponderName = (userID?: string) => {
        const normalizedUserID = userID?.trim();
        if (!normalizedUserID) return "El equipo";

        return adminNamesById[normalizedUserID]
            || (user?.uid === normalizedUserID ? user?.displayName?.trim() || user?.email?.trim() : "")
            || normalizedUserID;
    };
    const selectedResponseResponderName = resolveResponderName(selectedResponseConsulta?.respondedBy);
    const selectedResponseConsultaTitle = selectedResponseConsulta
        ? `**Consulta - ${selectedResponseConsulta.date} ${selectedResponseConsulta.time}**`
        : "**Consulta**";
    const selectedResponseSentTitle = selectedResponseConsulta
        ? `**Respuesta enviada por ${selectedResponseResponderName} - ${selectedResponseConsulta.responseDate} ${selectedResponseConsulta.responseTime}**`
        : "**Respuesta enviada**";

    const buildOutboundResponseMessage = (consulta: ConsultaRow, response: string) => {
        const collaboratorName = consulta.name?.trim() || "colaborador";
        const responderName = resolveResponderName(user?.uid) || user?.email?.trim() || "el equipo";
        const questionText = (consulta.message || "Sin texto de consulta").trim();
        const answerText = response.trim();
        const consultationDate = consulta.date?.trim();
        const consultationTime = consulta.time?.trim();
        const consultationContext = `Te responde ${responderName} sobre tu consulta realizada el dia ${consultationDate} a las ${consultationTime}:`;

        return [
            `Hola ${collaboratorName}.`,
            "",
            consultationContext,
            "",
            `"${questionText}"`,
            "",
            "Respuesta:",
            answerText,
        ].join("\n");
    };

    const mapRecordToRow = (record: QuestionRecord): ConsultaRow => {
        const message = extractMessageText(record.text);
        return {
            id: record.id,
            name: record.collaboratorName ?? "Sin nombre",
            date: formatDate(record.createdAt),
            time: formatTime(record.createdAt),
            responseDate: formatDate(record.respondedAt),
            responseTime: formatTime(record.respondedAt),
            respondedBy: record.respondedBy ?? "",
            preview: buildPreview(message),
            message,
            imageURL: record.collaboratorImageURL,
            read: Boolean(record.read),
            responded: Boolean(record.responded),
            archived: Boolean(record.archived),
            response: record.responseText ?? "",
        };
    };

    const fetchConsultas = useCallback(async () => {
        setLoading(true);
        setErrorMessage("");
        try {
            const apiFilter = getServiceFilter(selectedFilter.id);
            const result = await questionsService.getQuestionsByFilter(orgID, apiFilter);
            setConsultas(result.map(mapRecordToRow));
        } catch {
            setErrorMessage("No se pudieron cargar las consultas.");
        } finally {
            setLoading(false);
        }
    }, [selectedFilter.id, questionsService, orgID]);

    useEffect(() => {
        void fetchConsultas();
    }, [fetchConsultas]);

    useEffect(() => {
        let isMounted = true;

        const fetchAdminUsers = async () => {
            try {
                const adminUsers = await usersService.getAdminUsers(orgID);
                if (!isMounted) return;

                const nextAdminNames = adminUsers.reduce<Record<string, string>>((accumulator, adminUser) => {
                    if (adminUser.id?.trim() && adminUser.name?.trim()) {
                        accumulator[adminUser.id.trim()] = adminUser.name.trim();
                    }
                    return accumulator;
                }, {});

                setAdminNamesById(nextAdminNames);
            } catch {
                if (!isMounted) return;
                setAdminNamesById({});
            }
        };

        void fetchAdminUsers();

        return () => {
            isMounted = false;
        };
    }, [orgID, usersService]);

    const handleOpenModal = async (rowId: string) => {
        const rowSnapshot = findConsultaById(rowId);
        if (!rowSnapshot) return;
        if (rowSnapshot.responded) {
            handleOpenResponseModal(rowId);
            return;
        }
        setSelectedConsulta(rowSnapshot);
        try {
            await questionsService.markAsRead(rowId);
            await fetchConsultas();
        } catch {
            // Keep modal flow even if API call fails, so user can still read.
        }
        setResponseText("");
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedConsulta(null);
    };

    const handleSendResponse = async () => {
        if (!selectedConsulta) return;
        if (selectedConsulta.responded) {
            setFeedbackMessage("Esta consulta ya fue respondida");
            setShowFeedback(true);
            setIsModalOpen(false);
            return;
        }
        const trimmedResponse = responseText.trim();
        if (!trimmedResponse) return;
        try {
            const outboundMessage = buildOutboundResponseMessage(selectedConsulta, trimmedResponse);
            await questionsService.sendResponse(selectedConsulta.id, { text: outboundMessage });
        } catch {
            setFeedbackMessage("No se pudo enviar la respuesta");
            setShowFeedback(true);
            return;
        }
        setIsModalOpen(false);
        setSelectedConsulta(null);
        setResponseText("");
        setFeedbackMessage("¡Respuesta enviada!");
        setShowFeedback(true);
        void fetchConsultas();
    };

    const handleArchive = async (rowId: string) => {
        try {
            await questionsService.archiveQuestion(rowId);
        } catch {
            setFeedbackMessage("No se pudo archivar la consulta");
            setShowFeedback(true);
            return;
        }
        setFeedbackMessage("Consulta archivada");
        setShowFeedback(true);
        void fetchConsultas();
    };

    const handleUnarchive = async (rowId: string) => {
        try {
            await questionsService.unarchiveQuestion(rowId);
        } catch {
            setFeedbackMessage("No se pudo desarchivar la consulta");
            setShowFeedback(true);
            return;
        }
        setFeedbackMessage("Consulta desarchivada");
        setShowFeedback(true);
        void fetchConsultas();
    };

    const handleCloseFeedback = () => {
        setShowFeedback(false);
    };

    const handleOpenResponseModal = (rowId: string) => {
        const rowSnapshot = findConsultaById(rowId);
        if (!rowSnapshot) return;
        setSelectedResponseConsulta(rowSnapshot);
        setIsResponseModalOpen(true);
    };

    const handleCloseResponseModal = () => {
        setIsResponseModalOpen(false);
        setSelectedResponseConsulta(null);
    };

    return (
        <VerticalContainer gap={designSystemStyles.gapMd} className={styles.container} height="100%">
            <HorizontalContainer className={styles.toolbar}>
                <HorizontalContainer width="auto" gap={designSystemStyles.gapSm} className={styles.searchControls}>
                    <div className={styles.searchBarWrapper}>
                        <SearchBar
                            className={styles.searchBarControl}
                            placeholder="Buscar consulta"
                            value={searchQuery}
                            onChange={setSearchQuery}
                        />
                    </div>
                    <RefreshButton
                        text=""
                        onClick={fetchConsultas}
                        disabled={loading}
                        className={styles.refreshButton}
                    />
                </HorizontalContainer>
                <ComboBox
                    options={filterOptions}
                    value={selectedFilter}
                    placeholder="Seleccionar"
                    onChange={setSelectedFilter}
                    className={styles.comboBoxConsultas}
                />
            </HorizontalContainer>
            <VerticalContainer gap={designSystemStyles.gapSm} height="100%">
                <TableHeaderContainer gap={designSystemStyles.gapNone}>
                    <TableHeaderCell text="Colaborador" width={columnWidths.collaborator} />
                    <TableHeaderCell text="Fecha" width={columnWidths.date} />
                    <TableHeaderCell text="Hora" width={columnWidths.time} />
                    <TableHeaderCell text="Vista Previa" width={columnWidths.preview} />
                    <TableHeaderCell text="Acción" width={columnWidths.action} justifyContent="center" />
                </TableHeaderContainer>
                <VerticalContainer
                    gap={designSystemStyles.gapSm}
                    className={styles.tableBody}
                    scrollable={true}
                >
                    {loading ? (
                        <div className={styles.stateContainer}>
                            <Loader />
                        </div>
                    ) : !!errorMessage ? (
                        <Text text={errorMessage} color={designSystemStyles.colorError} />
                    ) : filteredConsultas.length === 0 ? (
                        <div className={styles.stateContainer}>
                            <div className={styles.emptyState}>
                                <QontySVG className={styles.emptyStateIcon} aria-hidden="true" />
                                <Text
                                    text="No hay consultas nuevas para mostrar."
                                    color={designSystemStyles.colorTextSecondary}
                                    className={styles.emptyStateText}
                                />
                            </div>
                        </div>
                    ) : (
                        filteredConsultas.map((row, index) => (
                            <TableRowContainer
                                key={row.id}
                                gap={designSystemStyles.gapNone}
                                backgroundColor={
                                    index % 2 === 1
                                        ? designSystemStyles.colorBackgroundGray2
                                        : designSystemStyles.colorBackgroundWhite
                                }
                            >
                                <HorizontalContainer width={columnWidths.collaborator} alignment="center">
                                    <UserCard name={row.name} imageURL={row.imageURL} />
                                </HorizontalContainer>
                                <HorizontalContainer width={columnWidths.date} alignment="center">
                                    <Text text={row.date} color={designSystemStyles.colorTextSecondary} />
                                </HorizontalContainer>
                                <HorizontalContainer width={columnWidths.time} alignment="center">
                                    <Text text={row.time} color={designSystemStyles.colorTextSecondary} />
                                </HorizontalContainer>
                                <HorizontalContainer width={columnWidths.preview} alignment="center">
                                    <Text
                                        text={row.preview}
                                        color={designSystemStyles.colorTextSecondary}
                                        className={styles.previewText}
                                    />
                                </HorizontalContainer>
                                <HorizontalContainer
                                    width={columnWidths.action}
                                    alignment="center"
                                    justifyContent="center"
                                    gap={designSystemStyles.gapSm}
                                >
                                    {!row.responded && (
                                        <Button
                                            text=""
                                            onClick={() => handleOpenModal(row.id)}
                                            backgroundColor="transparent"
                                            borderColor="transparent"
                                            width={designSystemStyles.sizeIconWidth}
                                            height={designSystemStyles.sizeIconHeight}
                                            className={styles.actionButton}
                                        >
                                            <SystemIcon
                                                type="eye"
                                                color={row.read ? designSystemStyles.colorTextSecondary : designSystemStyles.colorGreen}
                                                className={styles.actionIcon}
                                            />
                                        </Button>
                                    )}
                                    {!row.archived ? (
                                        <Button
                                            text=""
                                            onClick={() => handleArchive(row.id)}
                                            backgroundColor="transparent"
                                            borderColor="transparent"
                                            width={designSystemStyles.sizeIconWidth}
                                            height={designSystemStyles.sizeIconHeight}
                                            className={styles.actionButton}
                                        >
                                            <SystemIcon
                                                type="inbox"
                                                color={designSystemStyles.colorText}
                                                className={styles.actionIcon}
                                            />
                                        </Button>
                                    ) : (
                                        <Button
                                            text=""
                                            onClick={() => handleUnarchive(row.id)}
                                            backgroundColor="transparent"
                                            borderColor="transparent"
                                            width={designSystemStyles.sizeIconWidth}
                                            height={designSystemStyles.sizeIconHeight}
                                            className={styles.actionButton}
                                        >
                                            <SystemIcon
                                                type="inbox"
                                                color={designSystemStyles.colorMainAction}
                                                className={styles.actionIcon}
                                            />
                                        </Button>
                                    )}
                                    {row.responded && (
                                        <button
                                            type="button"
                                            className={styles.responseBadge}
                                            onClick={() => handleOpenResponseModal(row.id)}
                                        >
                                            R
                                        </button>
                                    )}
                                </HorizontalContainer>
                            </TableRowContainer>
                        ))
                    )}
                </VerticalContainer>
            </VerticalContainer>
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={`Consulta de ${selectedConsulta?.name ?? ""}`}
                width={modalSize.width}
                maxWidth={modalSize.maxWidth}
                maxHeight={modalSize.maxHeight}
            >
                <VerticalContainer gap={designSystemStyles.gapSm} className={styles.modalContent}>
                    <VerticalContainer gap={designSystemStyles.gapXxxSm} className={styles.messageBlock}>
                        <Text text="**Mensaje**" markdown={true} />
                        <Text
                            text={selectedConsultaMessage}
                            color={designSystemStyles.colorText}
                            className={styles.messageText}
                        />
                    </VerticalContainer>
                    <VerticalContainer gap={designSystemStyles.gapXxxSm}>
                        <Text text="**Respuesta**" markdown={true} />
                        <TextAreaInput
                            label=""
                            placeholder="Tu respuesta..."
                            value={responseText}
                            onChange={setResponseText}
                            className={styles.responseInput}
                        />
                    </VerticalContainer>
                    <HorizontalContainer
                        gap={designSystemStyles.gapSm}
                        justifyContent={designSystemStyles.justifyContentRight}
                    >
                        <HollowedButton
                            text="Cancelar"
                            onClick={handleCloseModal}
                            className={styles.modalActionButton}
                        />
                        <NormalButton
                            text="Responder y enviar"
                            onClick={handleSendResponse}
                            disabled={!responseText.trim()}
                            className={styles.modalActionButton}
                        />
                    </HorizontalContainer>
                </VerticalContainer>
            </Modal>
            <Modal
                isOpen={isResponseModalOpen}
                onClose={handleCloseResponseModal}
                title={`Respuesta a ${selectedResponseConsulta?.name ?? ""}`}
                width={modalSize.width}
                maxWidth={modalSize.maxWidth}
                maxHeight={modalSize.maxHeight}
            >
                <VerticalContainer gap={designSystemStyles.gapSm} className={styles.modalContent}>
                    <VerticalContainer gap={designSystemStyles.gapXxxSm} className={styles.messageBlock}>
                        <Text text={selectedResponseConsultaTitle} markdown={true} />
                        <Text
                            text={selectedResponseConsultaMessage || "Sin mensaje"}
                            color={designSystemStyles.colorText}
                            className={styles.messageText}
                        />
                    </VerticalContainer>
                    <VerticalContainer gap={designSystemStyles.gapXxxSm} className={styles.messageBlock}>
                        <Text text={selectedResponseSentTitle} markdown={true} />
                        <Text
                            text={selectedResponseText || "Sin respuesta"}
                            color={designSystemStyles.colorText}
                            className={styles.messageText}
                        />
                    </VerticalContainer>
                    <HorizontalContainer justifyContent={designSystemStyles.justifyContentRight} width="100%">
                        <HollowedButton onClick={handleCloseResponseModal} text="Cerrar" />
                    </HorizontalContainer>
                </VerticalContainer>
            </Modal>
            {showFeedback && (
                <div className={styles.feedbackOverlay} onClick={handleCloseFeedback}>
                    <div onClick={(event) => event.stopPropagation()}>
                        <CardContainer className={styles.feedbackCard} width="420px">
                            <HorizontalContainer gap={designSystemStyles.gapMd} alignment="center">
                                <span className={styles.feedbackIcon} aria-hidden="true" />
                                <Text text={feedbackMessage} color={designSystemStyles.colorMainAction} />
                            </HorizontalContainer>
                        </CardContainer>
                    </div>
                </div>
            )}
        </VerticalContainer>
    );
};

export default Consultas;
